import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const Field = ({ label, hint, required, children }) => (
  <div className="space-y-1.5">
    {label && (
      <label className="block text-sm font-semibold text-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, type = "text", ...rest }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full h-11 px-4 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
    {...rest}
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 3, ...rest }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-4 py-3 rounded-2xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-y"
    {...rest}
  />
);

// Bilingual pair: shows ru + en inputs side by side.
export const BilingualText = ({ label, hint, ruValue, enValue, onChange, multiline, rows = 3 }) => {
  const Comp = multiline ? TextArea : TextInput;
  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-semibold">{label}</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-muted-foreground mb-1">🇷🇺 Русский</div>
          <Comp value={ruValue} onChange={(v) => onChange("ru", v)} rows={rows} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">🇬🇧 English</div>
          <Comp value={enValue} onChange={(v) => onChange("en", v)} rows={rows} />
        </div>
      </div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
};

// Photo upload field
export const PhotoField = ({ label, value, onChange, hint, accept = "image/*,application/pdf" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Загружено");
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap items-center gap-3">
        {value && (
          <div className="relative size-16 rounded-xl overflow-hidden border border-border bg-muted shrink-0">
            {value.match(/\.(pdf)$/i) ? (
              <div className="w-full h-full flex items-center justify-center text-xs font-semibold">PDF</div>
            ) : (
              <img src={value} alt="" className="w-full h-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-0.5 right-0.5 size-5 rounded-full bg-background/90 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
              aria-label="Удалить"
            >
              <X className="size-3" />
            </button>
          </div>
        )}
        <input ref={ref} type="file" hidden onChange={upload} accept={accept} />
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <Upload className="size-4" />
          {busy ? "..." : value ? "Заменить" : "Загрузить файл"}
        </button>
        <TextInput
          value={value || ""}
          onChange={onChange}
          placeholder="или вставьте URL..."
        />
      </div>
    </Field>
  );
};

// List editor: add/remove/reorder items.
export const ListEditor = ({ label, hint, items, onChange, renderItem, defaultItem, itemTitle }) => {
  const safeItems = Array.isArray(items) ? items : [];
  const add = () => onChange([...safeItems, { ...defaultItem }]);
  const remove = (idx) => onChange(safeItems.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const arr = [...safeItems];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange(arr);
  };
  const update = (idx, patch) => {
    const arr = [...safeItems];
    arr[idx] = { ...arr[idx], ...patch };
    onChange(arr);
  };
  return (
    <div className="space-y-3">
      {label && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{label} <span className="text-muted-foreground text-xs font-normal">({safeItems.length})</span></div>
          <button type="button" onClick={add} className="text-xs font-semibold text-accent hover:underline">+ Добавить</button>
        </div>
      )}
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      <div className="space-y-3">
        {safeItems.map((it, i) => (
          <div key={i} className="p-4 rounded-2xl border border-border bg-muted/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-muted-foreground">
                {itemTitle ? itemTitle(it, i) : `#${i + 1}`}
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="size-7 rounded-md hover:bg-background disabled:opacity-30" title="Вверх">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === safeItems.length - 1} className="size-7 rounded-md hover:bg-background disabled:opacity-30" title="Вниз">↓</button>
                <button type="button" onClick={() => remove(i)} className="size-7 rounded-md text-destructive hover:bg-destructive hover:text-destructive-foreground" title="Удалить">×</button>
              </div>
            </div>
            {renderItem(it, (patch) => update(i, patch), i)}
          </div>
        ))}
        {safeItems.length === 0 && (
          <button type="button" onClick={add} className="w-full py-6 rounded-2xl border border-dashed border-border text-sm text-muted-foreground hover:bg-muted/40 hover:border-foreground/30">
            + Добавить первый элемент
          </button>
        )}
      </div>
    </div>
  );
};

export const Section = ({ title, description, children }) => (
  <div className="space-y-4 p-5 sm:p-6 rounded-3xl border border-border bg-card">
    {title && (
      <div>
        <div className="font-heading text-lg font-semibold">{title}</div>
        {description && <div className="text-sm text-muted-foreground mt-1">{description}</div>}
      </div>
    )}
    <div className="space-y-4">{children}</div>
  </div>
);
